import { computed, ref } from 'vue'
import { defineStore } from 'pinia'

export type AppNotificationType = 'incoming-call' | 'connected-call' | 'missed-call' | 'ended-call'
export type IncomingCallDecision = 'accepted' | 'rejected'

export interface IAppNotification {
  id: string
  type: AppNotificationType
  title: string
  message: string
  createdAt: string
  incomingCallNumber?: string
  incomingCallDecision?: IncomingCallDecision | null
  incomingCallDecisionAt?: string | null
}

const STORAGE_KEY = 'hub.notifications'
const MAX_NOTIFICATIONS = 200

export const useNotificationStore = defineStore('notifications', () => {
  const notifications = ref<IAppNotification[]>([])

  const loadNotifications = () => {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) {
      notifications.value = []
      return
    }

    try {
      const parsed = JSON.parse(raw) as IAppNotification[]
      notifications.value = Array.isArray(parsed) ? parsed : []
    } catch {
      notifications.value = []
    }
  }

  const persistNotifications = () => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications.value))
  }

  const addNotification = (
    payload: Pick<IAppNotification, 'type' | 'title' | 'message'> &
      Partial<Pick<IAppNotification, 'incomingCallNumber'>>,
  ): IAppNotification => {
    const notification: IAppNotification = {
      id:
        globalThis.crypto?.randomUUID?.() ??
        `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      type: payload.type,
      title: payload.title,
      message: payload.message,
      createdAt: new Date().toISOString(),
      incomingCallNumber: payload.incomingCallNumber,
      incomingCallDecision: null,
      incomingCallDecisionAt: null,
    }

    notifications.value = [notification, ...notifications.value].slice(0, MAX_NOTIFICATIONS)
    persistNotifications()
    return notification
  }

  const removeNotification = (id: string) => {
    notifications.value = notifications.value.filter((notification) => notification.id !== id)
    persistNotifications()
  }

  const clearNotifications = () => {
    notifications.value = []
    persistNotifications()
  }

  const resolveIncomingCallNotification = (id: string, decision: IncomingCallDecision) => {
    const target = notifications.value.find((notification) => notification.id === id)
    if (!target) return
    target.incomingCallDecision = decision
    target.incomingCallDecisionAt = new Date().toISOString()
    persistNotifications()
  }

  const totalNotifications = computed(() => notifications.value.length)

  loadNotifications()

  return {
    notifications,
    totalNotifications,
    loadNotifications,
    addNotification,
    resolveIncomingCallNotification,
    removeNotification,
    clearNotifications,
  }
})
